import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, FileText, Briefcase, Loader2, Search, ChevronDown, ChevronUp, Check, Plus, X, Home, Truck, ShoppingCart, Leaf, Factory, Fuel, Mountain, Scissors, Building2, Zap, CreditCard, Heart, Edit3, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import axios from 'axios';
import { toast } from 'sonner';
import DashboardLayout from '../components/DashboardLayout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// الأقسام الرئيسية لدائرة التنمية الاقتصادية رأس الخيمة
const DED_CATEGORIES = [
  { id: 'instant_license', name_ar: 'أنشطة الرخصة الفورية', name_en: 'Instant License Activities', icon: Zap, color: '#3B82F6' },
  { id: 'non_profit', name_ar: 'أنشطة غير ربحية', name_en: 'Non-profit Activities', icon: Heart, color: '#EC4899' },
  { id: 'tomorrow_license', name_ar: 'أنشطة رخصة الغد (للعمل من المنزل)', name_en: 'Tomorrow License (Home Work)', icon: Home, color: '#10B981' },
  { id: 'mobile_carts', name_ar: 'أنشطة العربات المتنقلة', name_en: 'Mobile Cart Activities', icon: Truck, color: '#F59E0B' },
  { id: 'popular_goods', name_ar: 'أنشطة رخصة السلع والمنتجات الشعبية', name_en: 'Popular Goods License', icon: ShoppingCart, color: '#8B5CF6' },
  { id: 'resident_opportunity', name_ar: 'أنشطة رخصة فرصة للمقيمين (للعمل من المنزل)', name_en: 'Resident Opportunity License', icon: Building2, color: '#06B6D4' },
  { id: 'agriculture', name_ar: 'الزراعة والانتاج الحيواني', name_en: 'Agriculture & Animal Production', icon: Leaf, color: '#22C55E' },
  { id: 'ecommerce', name_ar: 'أنشطة رخصة التجارة الالكترونية', name_en: 'E-commerce License Activities', icon: CreditCard, color: '#EF4444' },
  { id: 'oil_gas', name_ar: 'النفط والغاز والخدمات المتعلقة', name_en: 'Oil & Gas Services', icon: Fuel, color: '#1E293B' },
  { id: 'mining', name_ar: 'التعدين واستغلال المحاجر', name_en: 'Mining & Quarrying', icon: Mountain, color: '#78716C' },
  { id: 'textiles', name_ar: 'تفصيل وخياطة المنسوجات', name_en: 'Textile Tailoring', icon: Scissors, color: '#A855F7' },
  { id: 'industries', name_ar: 'الصناعات', name_en: 'Industries', icon: Factory, color: '#0EA5E9' },
];

export default function DEDRAKSetup() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const { language } = useLanguage();
  
  const [activeView, setActiveView] = useState('categories'); // categories, category_activities
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [activities, setActivities] = useState([]);
  const [loadingActivities, setLoadingActivities] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Activity Modal State
  const [showActivityModal, setShowActivityModal] = useState(false);
  const [editingActivity, setEditingActivity] = useState(null);
  const [savingActivity, setSavingActivity] = useState(false);
  const [newActivity, setNewActivity] = useState({
    category_id: '',
    name_ar: '',
    name_en: '',
    code: '',
    is_active: true
  });
  
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (activeView === 'category_activities' && selectedCategory) {
      fetchCategoryActivities(selectedCategory.id);
    }
  }, [activeView, selectedCategory]);

  const fetchCategoryActivities = async (categoryId) => {
    setLoadingActivities(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/ded-activities?category_id=${categoryId}`, { headers });
      setActivities(res.data.data || []);
    } catch (err) {
      console.error('Error fetching activities:', err);
      setActivities([]);
    } finally {
      setLoadingActivities(false);
    }
  };

  const openCategory = (category) => {
    setSelectedCategory(category);
    setActiveView('category_activities');
  };

  const openAddActivityModal = () => {
    setEditingActivity(null);
    setNewActivity({
      category_id: selectedCategory?.id || '',
      name_ar: '',
      name_en: '',
      code: '',
      is_active: true
    });
    setShowActivityModal(true);
  };

  const openEditActivityModal = (activity) => {
    setEditingActivity(activity);
    setNewActivity({
      category_id: activity.category_id || selectedCategory?.id || '',
      name_ar: activity.name_ar || '',
      name_en: activity.name_en || '',
      code: activity.code || '',
      is_active: activity.is_active !== false
    });
    setShowActivityModal(true);
  };

  const saveActivity = async () => {
    if (!newActivity.name_ar || !newActivity.name_en) {
      toast.error(language === 'ar' ? 'يرجى إدخال اسم النشاط' : 'Please enter activity name');
      return;
    }

    if (!newActivity.category_id) {
      toast.error(language === 'ar' ? 'يرجى اختيار القسم' : 'Please select category');
      return;
    }

    setSavingActivity(true);
    try {
      if (editingActivity) {
        await axios.put(`${BACKEND_URL}/api/admin/ded-activities/${editingActivity.id}`, newActivity, { headers });
        toast.success(language === 'ar' ? 'تم تحديث النشاط بنجاح' : 'Activity updated successfully');
      } else {
        await axios.post(`${BACKEND_URL}/api/admin/ded-activities`, newActivity, { headers });
        toast.success(language === 'ar' ? 'تم إضافة النشاط بنجاح' : 'Activity added successfully');
      }

      setShowActivityModal(false);
      if (selectedCategory) {
        fetchCategoryActivities(selectedCategory.id);
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      let errorMessage = language === 'ar' ? 'فشل في حفظ النشاط' : 'Failed to save activity';
      if (typeof detail === 'string') errorMessage = detail;
      else if (Array.isArray(detail)) errorMessage = detail.map(d => d.msg || d).join(', ');
      toast.error(errorMessage);
    } finally {
      setSavingActivity(false);
    }
  };

  const deleteActivity = async (activityId) => {
    if (!confirm(language === 'ar' ? 'هل أنت متأكد من حذف هذا النشاط؟' : 'Are you sure you want to delete this activity?')) {
      return;
    }

    try {
      await axios.delete(`${BACKEND_URL}/api/admin/ded-activities/${activityId}`, { headers });
      toast.success(language === 'ar' ? 'تم حذف النشاط بنجاح' : 'Activity deleted successfully');
      if (selectedCategory) {
        fetchCategoryActivities(selectedCategory.id);
      }
    } catch (err) {
      toast.error(language === 'ar' ? 'فشل في حذف النشاط' : 'Failed to delete activity');
    }
  };

  const toggleActivityStatus = async (activity) => {
    try {
      await axios.put(`${BACKEND_URL}/api/admin/ded-activities/${activity.id}`, {
        ...activity,
        is_active: !activity.is_active
      }, { headers });
      fetchCategoryActivities(selectedCategory.id);
    } catch (err) {
      toast.error(language === 'ar' ? 'فشل في تحديث الحالة' : 'Failed to update status');
    }
  };

  // Build breadcrumb items dynamically
  const getBreadcrumbItems = () => {
    const items = [
      { labelAr: 'CLOUDGM FZ LLC', labelEn: 'CLOUDGM FZ LLC', path: '/dashboard?view=cloudgm' },
      { labelAr: 'منصة العملاء', labelEn: 'Customers Platform', path: '/dashboard?view=cloudgm&platform=customers' },
      { labelAr: 'التحكم والمعلومات (منصة العملاء)', labelEn: 'Control & Info (Customers)', path: '/control/cloudgm' },
      { labelAr: 'تأسيس الشركات', labelEn: 'Company Setup', path: '/control/company-setup' },
      { labelAr: 'إمارة رأس الخيمة', labelEn: 'Ras Al Khaimah', path: '/control/company-setup/ras-al-khaimah' },
      { labelAr: 'دائرة التنمية الاقتصادية', labelEn: 'DED' }
    ];
    
    if (activeView === 'category_activities' && selectedCategory) {
      items.push({ labelAr: selectedCategory.name_ar, labelEn: selectedCategory.name_en });
    }
    
    return items;
  };

  // Get subtitle based on active view
  const getSubtitle = () => {
    if (activeView === 'category_activities' && selectedCategory) {
      return { ar: selectedCategory.name_ar, en: selectedCategory.name_en };
    }
    return { ar: 'اختر قسم الأنشطة', en: 'Select activity category' };
  };

  // Handle back navigation
  const handleBack = () => {
    if (activeView === 'categories') {
      navigate('/control/company-setup/ras-al-khaimah');
    } else {
      setActiveView('categories');
      setSelectedCategory(null);
    }
  };

  // Filter activities based on search
  const filteredActivities = activities.filter(act => {
    if (!searchQuery) return true;
    return act.name_ar?.toLowerCase().includes(searchQuery.toLowerCase()) ||
           act.name_en?.toLowerCase().includes(searchQuery.toLowerCase()) ||
           act.code?.toLowerCase().includes(searchQuery.toLowerCase());
  });

  const subtitle = getSubtitle();

  return (
    <DashboardLayout
      title="دائرة التنمية الاقتصادية رأس الخيمة"
      titleEn="DED Ras Al Khaimah"
      subtitle={subtitle.ar}
      subtitleEn={subtitle.en}
      onBack={handleBack}
      breadcrumb={getBreadcrumbItems()}
    >
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Categories View */}
        {activeView === 'categories' && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {DED_CATEGORIES.map((category) => {
              const Icon = category.icon;
              return (
                <div
                  key={category.id}
                  onClick={() => openCategory(category)}
                  className="group bg-white/[0.02] backdrop-blur-xl rounded-2xl p-6 border border-white/10 hover:border-[#d4af37]/50 transition-all duration-300 cursor-pointer hover:-translate-y-1 hover:shadow-lg"
                  data-testid={`ded-category-${category.id}`}
                >
                  <div className="text-center">
                    <div 
                      className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300 border"
                      style={{ backgroundColor: `${category.color}15`, borderColor: `${category.color}30` }}
                    >
                      <Icon className="h-8 w-8" style={{ color: category.color }} />
                    </div>
                    <h3 className="text-sm font-bold text-white leading-tight group-hover:text-[#d4af37] transition-colors">
                      {language === 'ar' ? category.name_ar : category.name_en}
                    </h3>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Category Activities View */}
        {activeView === 'category_activities' && selectedCategory && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                {(() => {
                  const Icon = selectedCategory.icon;
                  return (
                    <div 
                      className="w-10 h-10 rounded-xl flex items-center justify-center"
                      style={{ backgroundColor: `${selectedCategory.color}20` }}
                    >
                      <Icon className="h-5 w-5" style={{ color: selectedCategory.color }} />
                    </div>
                  );
                })()}
                <h2 className="text-xl font-bold text-white">
                  {language === 'ar' ? selectedCategory.name_ar : selectedCategory.name_en}
                </h2>
              </div>
              
              <Button
                onClick={openAddActivityModal}
                className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl gap-2"
                data-testid="add-activity-btn"
              >
                <Plus className="h-4 w-4" />
                {language === 'ar' ? 'إضافة نشاط' : 'Add Activity'}
              </Button>
            </div>

            {/* Search */}
            <div className="bg-[#1a1a2e] rounded-2xl p-4 border border-[#2a2a4e] mb-6">
              <div className="relative">
                <Search className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#606060]" />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={language === 'ar' ? 'بحث عن نشاط...' : 'Search activities...'}
                  className="bg-[#0a0a0a] border-[#2a2a4e] text-white pr-10 rounded-xl"
                />
              </div>
            </div>

            {/* Activities Table */}
            {loadingActivities ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="h-8 w-8 animate-spin text-emerald-500" />
              </div>
            ) : filteredActivities.length === 0 ? (
              <div className="text-center py-16 text-[#808080] bg-[#1a1a2e] rounded-2xl border border-[#2a2a4e]">
                <Briefcase className="h-16 w-16 mx-auto mb-4 opacity-30" style={{ color: selectedCategory.color }} />
                <p className="text-lg">{language === 'ar' ? 'لا توجد أنشطة في هذا القسم' : 'No activities in this category'}</p>
                <p className="text-sm mt-2 text-[#606060]">
                  {language === 'ar' ? 'اضغط على "إضافة نشاط" لإضافة نشاط جديد' : 'Click "Add Activity" to add a new activity'}
                </p>
              </div>
            ) : (
              <div className="bg-[#1a1a2e] rounded-2xl border border-[#2a2a4e] overflow-hidden">
                {/* Table Header */}
                <div className="grid grid-cols-12 gap-4 p-4 bg-[#0a0a0a] border-b border-[#2a2a4e] text-sm font-semibold text-[#808080]">
                  <div className="col-span-4">{language === 'ar' ? 'اسم النشاط' : 'Activity Name'}</div>
                  <div className="col-span-3">{language === 'ar' ? 'الاسم بالإنجليزية' : 'English Name'}</div>
                  <div className="col-span-2 text-center">{language === 'ar' ? 'الرمز' : 'Code'}</div>
                  <div className="col-span-1 text-center">{language === 'ar' ? 'الحالة' : 'Status'}</div>
                  <div className="col-span-2 text-center">{language === 'ar' ? 'إجراءات' : 'Actions'}</div>
                </div>

                {/* Table Body */}
                <div className="divide-y divide-[#2a2a4e]">
                  {filteredActivities.map((activity) => (
                    <div key={activity.id} className="grid grid-cols-12 gap-4 p-4 items-center hover:bg-[#0a0a0a]/50 transition-colors">
                      <div className="col-span-4">
                        <p className="font-medium text-white">{activity.name_ar}</p>
                      </div>
                      <div className="col-span-3">
                        <p className="text-sm text-[#808080]" dir="ltr">{activity.name_en}</p>
                      </div>
                      <div className="col-span-2 text-center">
                        <span className="px-2 py-1 bg-[#0a0a0a] rounded-lg text-xs text-[#808080]">
                          {activity.code || '-'}
                        </span>
                      </div>
                      <div className="col-span-1 flex justify-center">
                        <button
                          onClick={() => toggleActivityStatus(activity)}
                          className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all ${
                            activity.is_active
                              ? 'bg-emerald-500/20 hover:bg-emerald-500/30'
                              : 'bg-[#0a0a0a] hover:bg-[#2a2a4e]'
                          }`}
                        >
                          <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center ${
                            activity.is_active
                              ? 'border-emerald-500 bg-emerald-500'
                              : 'border-[#606060]'
                          }`}>
                            {activity.is_active && (
                              <Check className="h-3 w-3 text-white" />
                            )}
                          </div>
                        </button>
                      </div>
                      <div className="col-span-2 flex justify-center gap-2">
                        <button
                          onClick={() => openEditActivityModal(activity)}
                          className="w-8 h-8 rounded-lg bg-[#0a0a0a] flex items-center justify-center hover:bg-blue-500/20 transition-colors"
                        >
                          <Edit3 className="h-4 w-4 text-[#808080] hover:text-blue-400" />
                        </button>
                        <button
                          onClick={() => deleteActivity(activity.id)}
                          className="w-8 h-8 rounded-lg bg-[#0a0a0a] flex items-center justify-center hover:bg-red-500/20 transition-colors"
                        >
                          <Trash2 className="h-4 w-4 text-[#808080] hover:text-red-400" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

      {/* Add/Edit Activity Modal */}
      {showActivityModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-[#1a1a2e] rounded-2xl border border-[#2a2a4e] w-full max-w-lg">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-[#2a2a4e]">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-emerald-500/20 rounded-xl flex items-center justify-center">
                  <Briefcase className="h-5 w-5 text-emerald-500" />
                </div>
                <h2 className="text-lg font-bold text-white">
                  {editingActivity 
                    ? (language === 'ar' ? 'تعديل النشاط' : 'Edit Activity')
                    : (language === 'ar' ? 'إضافة نشاط جديد' : 'Add New Activity')
                  }
                </h2>
              </div>
              <button
                onClick={() => setShowActivityModal(false)}
                className="w-8 h-8 rounded-lg bg-[#0a0a0a] flex items-center justify-center hover:bg-red-500/20 transition-colors"
              >
                <X className="h-4 w-4 text-[#808080]" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 space-y-4">
              {/* القسم الرئيسي */}
              <div>
                <label className="block text-sm text-[#808080] mb-2">
                  {language === 'ar' ? 'القسم الرئيسي' : 'Main Category'} *
                </label>
                <select
                  value={newActivity.category_id}
                  onChange={(e) => setNewActivity({ ...newActivity, category_id: e.target.value })}
                  className="w-full bg-[#0a0a0a] border border-[#2a2a4e] rounded-xl px-4 py-3 text-white focus:border-[#c9a227] focus:outline-none"
                  data-testid="activity-category"
                >
                  <option value="">{language === 'ar' ? 'اختر القسم' : 'Select Category'}</option>
                  {DED_CATEGORIES.map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {language === 'ar' ? cat.name_ar : cat.name_en}
                    </option>
                  ))}
                </select>
              </div>

              {/* اسم النشاط بالعربية */}
              <div>
                <label className="block text-sm text-[#808080] mb-2">
                  {language === 'ar' ? 'اسم النشاط بالعربية' : 'Activity Name (Arabic)'} *
                </label>
                <Input
                  value={newActivity.name_ar}
                  onChange={(e) => setNewActivity({ ...newActivity, name_ar: e.target.value })}
                  placeholder="مثال: تجارة المواد الغذائية"
                  className="bg-[#0a0a0a] border-[#2a2a4e] text-white rounded-xl"
                  data-testid="activity-name-ar"
                />
              </div>

              {/* اسم النشاط بالإنجليزية */}
              <div>
                <label className="block text-sm text-[#808080] mb-2">
                  {language === 'ar' ? 'اسم النشاط بالإنجليزية' : 'Activity Name (English)'} *
                </label>
                <Input
                  value={newActivity.name_en}
                  onChange={(e) => setNewActivity({ ...newActivity, name_en: e.target.value })}
                  placeholder="Example: Food Trading"
                  className="bg-[#0a0a0a] border-[#2a2a4e] text-white rounded-xl"
                  dir="ltr"
                  data-testid="activity-name-en"
                />
              </div>

              {/* رمز النشاط */}
              <div>
                <label className="block text-sm text-[#808080] mb-2">
                  {language === 'ar' ? 'رمز النشاط (اختياري)' : 'Activity Code (Optional)'}
                </label>
                <Input
                  value={newActivity.code}
                  onChange={(e) => setNewActivity({ ...newActivity, code: e.target.value })}
                  placeholder="مثال: ACT001"
                  className="bg-[#0a0a0a] border-[#2a2a4e] text-white rounded-xl"
                  dir="ltr"
                  data-testid="activity-code"
                />
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-end gap-3 p-6 border-t border-[#2a2a4e]">
              <Button
                onClick={() => setShowActivityModal(false)}
                variant="outline"
                className="border-[#2a2a4e] text-white hover:bg-[#2a2a4e]"
              >
                {language === 'ar' ? 'إلغاء' : 'Cancel'}
              </Button>
              <Button
                onClick={saveActivity}
                disabled={savingActivity}
                className="bg-emerald-500 hover:bg-emerald-600 text-white gap-2"
                data-testid="save-activity-btn"
              >
                {savingActivity ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Check className="h-4 w-4" />
                )}
                {editingActivity 
                  ? (language === 'ar' ? 'تحديث' : 'Update')
                  : (language === 'ar' ? 'إضافة' : 'Add')
                }
              </Button>
            </div>
          </div>
        </div>
      )}
      </section>
    </DashboardLayout>
  );
}
